/**
 * Replays (06 §R5): a mission is its definition plus the command fed to the sim on every tick.
 * Commands are quantised BEFORE the sim sees them, live as well as on playback, so what was
 * recorded is exactly what was played; identical consecutive ticks are run-length encoded.
 * Pure: the recorder and the file format, nothing about where replays are kept.
 */

import type { Command, WeaponId } from '../sim/types';

const WEAPONS: readonly WeaponId[] = ['carbine', 'grenade', 'law', 'bomb', 'knife', 'pistol', 'smoke'];
// ROUND7: `sneak` is bit 8, after everything else, so a recording made before it decodes with sneak off.
const FLAGS = ['run', 'crawl', 'fire', 'use', 'reload', 'firstAid', 'interact', 'recall', 'sneak'] as const;

const MOVE_STEPS = 127;
const ANGLE_STEPS = 10000;
const DIST_STEPS = 100;

/** `[ticks, moveX, moveZ, aim, aimDist, flags, select, fuse, aimAssist]`, all integers. */
export type ReplayRun = [number, number, number, number, number, number, number, number, number];

const q = (value: number, steps: number): number => Math.round(value * steps) + 0;

function pack(cmd: Command): ReplayRun {
  let flags = 0;
  FLAGS.forEach((name, bit) => {
    if (cmd[name]) flags |= 1 << bit;
  });
  return [
    1, q(cmd.moveX, MOVE_STEPS), q(cmd.moveZ, MOVE_STEPS), q(cmd.aim, ANGLE_STEPS), q(cmd.aimDist, DIST_STEPS),
    flags, cmd.select === null ? -1 : WEAPONS.indexOf(cmd.select), cmd.fuse, q(cmd.aimAssist, ANGLE_STEPS),
  ];
}

function unpack(run: ReplayRun): Command {
  const [, mx, mz, aim, dist, flags, select, fuse, assist] = run;
  const flag = (bit: number): boolean => (flags & (1 << bit)) !== 0;
  return {
    moveX: mx / MOVE_STEPS, moveZ: mz / MOVE_STEPS, aim: aim / ANGLE_STEPS, aimDist: dist / DIST_STEPS,
    run: flag(0), crawl: flag(1), fire: flag(2), use: flag(3), reload: flag(4), firstAid: flag(5),
    interact: flag(6), recall: flag(7), sneak: flag(8),
    select: select < 0 ? null : (WEAPONS[select] ?? null),
    fuse: fuse === 5 || fuse === 15 ? fuse : 10,
    aimAssist: assist / ANGLE_STEPS,
  };
}

/** The command the sim is given: what a replay can store exactly. Idempotent. */
export function quantise(cmd: Command): Command {
  return unpack(pack(cmd));
}

/** Collects one command per tick, in order, from tick 0. */
export class ReplayRecorder {
  readonly runs: ReplayRun[] = [];
  private count = 0;

  get ticks(): number {
    return this.count;
  }

  /** `cmd` must already be quantised. */
  push(cmd: Command): void {
    const next = pack(cmd);
    const last = this.runs[this.runs.length - 1];
    this.count++;
    if (last && next.every((v, i) => i === 0 || v === last[i])) last[0]++;
    else this.runs.push(next);
  }
}

/** One command per tick, expanded from the runs. */
export function expand(runs: readonly ReplayRun[]): Command[] {
  const out: Command[] = [];
  for (const run of runs) {
    const cmd = unpack(run);
    for (let i = 0; i < run[0]; i++) out.push(cmd);
  }
  return out;
}
